import { inject, Injectable } from '@angular/core';
import type { MigrationInfo } from '@checkpoint/shared';
import { DatabaseService } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Apply migrations received from server
 * Phase 6: Schema Migration Delivery
 */
@Injectable({
  providedIn: 'root',
})
export class MigrationApplierService {
  private db = inject(DatabaseService);
  private log = inject(LoggerService).getLogger('migration-applier');

  /**
   * Apply a batch of migrations
   */
  async applyMigrations(migrations: MigrationInfo[]): Promise<void> {
    if (migrations.length === 0) {
      return;
    }

    this.log.info(`Applying ${migrations.length} migration(s)...`);

    for (const migration of migrations) {
      await this.applyMigration(migration);
    }

    this.log.info('✅ All migrations applied');
  }

  /**
   * Apply a single migration
   */
  private async applyMigration(migration: MigrationInfo): Promise<void> {
    this.log.info(`Applying migration: ${migration.description} (${migration.filename})`);

    try {
      // Check if migration already applied (idempotency guard)
      const existing = await this.db.exec('SELECT version FROM _migrations WHERE version = ?', [
        migration.version,
      ]);
      if (existing && existing.length > 0) {
        this.log.debug(
          `⏭️  Skipping already-applied migration: ${migration.filename} (v${migration.version})`,
        );
        return;
      }

      // Verify checksum
      const checksum = await this.calculateChecksum(migration.sql);
      if (checksum !== migration.checksum) {
        throw new Error('Checksum mismatch - migration may be corrupted');
      }

      // Execute all statements + record in _migrations atomically.
      // Without a transaction, partial application (DDL applied but process dies
      // before INSERT INTO _migrations) causes the server to resend the migration
      // on every reconnect, hitting "duplicate column name" forever.
      await this.db.run('BEGIN;');
      try {
        const statements = this.splitSqlStatements(migration.sql);
        for (const stmt of statements) {
          if (stmt.trim()) {
            try {
              await this.db.run(stmt);
            } catch (stmtError: any) {
              // ALTER TABLE ADD COLUMN on an already-existing column is expected
              // when the base schema was updated alongside the migration.
              const msg = stmtError?.message || String(stmtError);
              if (msg.includes('duplicate column name')) {
                this.log.debug(`⏭️  Column already exists, skipping: ${stmt.substring(0, 80)}...`);
                continue;
              }
              throw stmtError;
            }
          }
        }

        // Record migration in _migrations table (inside the transaction)
        await this.db.run(
          `INSERT INTO _migrations (version, filename, applied_at) VALUES (?, ?, ?)`,
          [migration.version, migration.filename, Date.now()],
        );

        await this.db.run('COMMIT;');
      } catch (txError) {
        try {
          await this.db.run('ROLLBACK;');
        } catch {}
        throw txError;
      }

      this.log.info(`✅ Applied: ${migration.filename}`);
    } catch (error: any) {
      this.log.error(`❌ Migration failed: ${migration.filename}`, error);
      throw error;
    }
  }

  /**
   * Split SQL into individual statements
   * Simple implementation - handles semicolon-separated statements
   */
  private splitSqlStatements(sql: string): string[] {
    // Split on semicolons, but preserve them
    return sql
      .split(';')
      .map((stmt) => stmt.trim())
      .filter((stmt) => stmt.length > 0)
      .map((stmt) => stmt + ';');
  }

  /**
   * Calculate SHA-256 checksum
   */
  private async calculateChecksum(content: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(content);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Get current schema version
   */
  async getCurrentVersion(): Promise<number> {
    const result = await this.db.exec('SELECT MAX(version) as version FROM _schema_version');
    return (result[0] as any)?.version || 0;
  }
}
